import { denied, violation } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const savedItemTargetTypes = Object.freeze(["CATALOG_ITEM", "ITINERARY", "TRIP"]);
export const savedChannels = Object.freeze(["CUSTOMER_APP", "CONCIERGE_AGENT"]);
/** The longest title snapshot, counted in code points as `char_length` counts them. */
export const SAVED_TITLE_SNAPSHOT_MAX_CHARACTERS = 200;
/**
 * Whether the saved target can still be opened: a catalog item only while PUBLISHED, an itinerary
 * unless it was superseded or archived, a trip unless it was cancelled. An unavailable target is
 * shown with its stored snapshot title instead of failing.
 */
export function availabilityOf(target) {
    switch (target.type) {
        case "CATALOG_ITEM":
            return target.status === "PUBLISHED" ? "AVAILABLE" : "UNAVAILABLE";
        case "ITINERARY":
            return target.status === "SUPERSEDED" || target.status === "ARCHIVED"
                ? "UNAVAILABLE"
                : "AVAILABLE";
        case "TRIP":
            return target.status === "CANCELLED" ? "UNAVAILABLE" : "AVAILABLE";
    }
}
function snapshotOf(title) {
    return [...title.trim()].slice(0, SAVED_TITLE_SNAPSHOT_MAX_CHARACTERS).join("");
}
/**
 * Plans a save. A customer may save a PUBLISHED catalog item, their own ACTIVE itinerary and their
 * own trip unless it was cancelled; anything else is `saved_item.target_unavailable`, and another
 * customer's itinerary or trip is `not_owner`. A saved item references the canonical row and copies
 * no prose beyond the bounded snapshot title.
 */
export function planSave(input) {
    const { customerId, channel, target } = input;
    if (target.type !== "CATALOG_ITEM" && target.customerId !== customerId)
        throw denied("not_owner");
    const eligible = target.type === "CATALOG_ITEM"
        ? target.status === "PUBLISHED"
        : target.type === "ITINERARY"
            ? target.status === "ACTIVE"
            : target.status !== "CANCELLED";
    if (!eligible)
        throw violation("saved_item.target_unavailable");
    return Object.freeze({
        targetType: target.type,
        targetId: target.id,
        catalogItemId: target.type === "CATALOG_ITEM" ? target.id : null,
        itineraryId: target.type === "ITINERARY" ? target.id : null,
        tripId: target.type === "TRIP" ? target.id : null,
        titleSnapshot: snapshotOf(target.type === "CATALOG_ITEM" ? target.name : target.title),
        catalogItemTypeSnapshot: target.type === "CATALOG_ITEM" ? target.itemType : null,
        savedChannel: channel,
    });
}
